/** Product subtotal for the selected quantity, excluding delivery. */
class DirectJantesProductTotal extends HTMLElement {
  #controller;
  #observer;

  connectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#controller = new AbortController();
    const root = this.closest('.product-information');
    const form = root?.querySelector('.product-details product-form-component');
    const label = this.querySelector('[data-dj-total-label]');
    const amount = this.querySelector('[data-dj-total-value]');
    if (!form || !label || !amount) return;
    const update = () => {
      const quantity = Number(form.querySelector('input[name="quantity"]')?.value);
      const unit = this.dataset.unitCents === '' ? NaN : Number(this.dataset.unitCents);
      const total = unit * quantity;
      const sameVariant = form.querySelector('input[name="id"]')?.value === this.dataset.variantId;
      const valid = sameVariant && Number.isSafeInteger(quantity) && quantity > 0
        && Number.isSafeInteger(unit) && unit >= 0 && Number.isSafeInteger(total);
      this.hidden = !valid;
      if (!valid) return;
      const nextLabel = `Total pour ${quantity} ${quantity === 1 ? 'jante' : 'jantes'} :`;
      const nextAmount = `${(total / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € HT`;
      if (label.textContent !== nextLabel) label.textContent = nextLabel;
      if (amount.textContent !== nextAmount) amount.textContent = nextAmount;
    };
    for (const event of ['input', 'change', 'quantity-selector:update']) {
      form.addEventListener(event, update, { signal: this.#controller.signal });
    }
    window.addEventListener('pageshow', update, { signal: this.#controller.signal });
    this.#observer = new MutationObserver(update);
    this.#observer.observe(form, { subtree: true, childList: true, attributes: true,
      attributeFilter: ['value', 'min', 'max', 'step', 'disabled', 'data-cart-quantity'] });
    this.#observer.observe(this, { attributes: true, attributeFilter: ['data-unit-cents', 'data-variant-id'] });
    update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }
}

if (!customElements.get('directjantes-product-total')) {
  customElements.define('directjantes-product-total', DirectJantesProductTotal);
}

/** Delivery estimate scoped to the main product form. Cart quantities cannot change it. */
class DirectJantesProductShipping extends HTMLElement {
  #controller;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const root = this.closest('.product-information');
    const form = root?.querySelector('product-form-component');
    if (!form) return;
    const update = () => {
      const input = form.querySelector('input[name="quantity"]');
      const quantity = Number(input?.value);
      const unit = Number(this.dataset.unitCents);
      const selection = this.querySelector('[data-dj-shipping-selection]');
      const total = this.querySelector('[data-dj-shipping-total]');
      if (!selection || !total) return;
      const valid = Number.isSafeInteger(quantity) && quantity > 0 && Number.isFinite(unit) && unit > 0;
      selection.hidden = !valid;
      if (valid) {
        const amount = (unit * quantity / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        total.textContent = `${amount} € HT pour ${quantity} ${quantity > 1 ? 'jantes' : 'jante'}`;
      }
    };
    for (const event of ['input', 'change', 'quantity-selector:update']) {
      form.addEventListener(event, update, { signal: this.#controller.signal });
    }
    update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }
}

if (!customElements.get('directjantes-product-shipping')) {
  customElements.define('directjantes-product-shipping', DirectJantesProductShipping);
}

/** All rows remain readable if JavaScript is unavailable. */
class DirectJantesProductDimensions extends HTMLElement {
  #controller;
  #expanded = false;
  #staggered = false;
  #pairHandle = null;
  #rows = [];
  #button;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#button = this.querySelector('[data-dj-dimensions-toggle]');
    this.#rows = [...this.querySelectorAll('tbody tr')];
    if (!this.#button || this.#rows.length <= 2) return;

    this.#button.addEventListener('click', () => {
      this.#expanded = !this.#expanded;
      this.#updateRows();
    }, { signal: this.#controller.signal });
    this.#updateRows();
    this.#button.hidden = false;
  }

  /** Keep the current row and a different width visible without lengthening the table. */
  setStaggeredMode(active, pairHandle = null) {
    this.#staggered = active;
    this.#pairHandle = pairHandle;
    this.#updateRows();
  }

  #updateRows() {
    if (!this.#button || this.#rows.length <= 2) return;
    let visible = this.#rows.slice(0, 2);
    if (this.#staggered) {
      const width = row => {
        const value = (row.dataset.djWidth || '').trim().replace(',', '.');
        return /^\d+(?:\.\d+)?$/.test(value) && Number(value) > 0 ? Number(value) : null;
      };
      const current = this.#rows.find(row => row.classList.contains('is-current'));
      const currentWidth = current && width(current);
      const alternatives = currentWidth ? this.#rows.filter(row => row !== current && row.querySelector('a[href]')
        && width(row) && width(row) !== currentWidth) : [];
      const alternative = alternatives.find(row => row.dataset.djHandle === this.#pairHandle)
        || alternatives.find(row => row.hasAttribute('data-dj-stock') && Number(row.dataset.djStock) >= 2)
        || alternatives[0];
      if (current && alternative) visible = [current, alternative];
    }
    for (const row of this.#rows) {
      const hidden = !this.#expanded && !visible.includes(row);
      if (row.hidden !== hidden) row.hidden = hidden;
    }
    this.#button.setAttribute('aria-expanded', String(this.#expanded));
    this.#button.textContent = this.#expanded ? 'Afficher moins' : `Afficher plus (${this.#rows.length - 2})`;
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }
}

if (!customElements.get('directjantes-product-dimensions')) {
  customElements.define('directjantes-product-dimensions', DirectJantesProductDimensions);
}

/** Quantity shortcuts only: the native form and cart remain responsible for the order. */
class DirectJantesProductQuantity extends HTMLElement {
  #controller;
  #observer;

  connectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#controller = new AbortController();
    const root = this.closest('.product-information');
    const form = root?.querySelector('.product-details product-form-component');
    const presets = this.querySelector('[data-dj-quantity-presets]');
    const fallback = this.querySelector('[data-dj-quantity-fallback]');
    const help = this.querySelector('[data-dj-quantity-help]');
    const buttons = [...this.querySelectorAll('[data-dj-quantity]')];
    if (!form || !presets || !fallback || !help) return;

    const widthNumber = value => {
      const text = String(value ?? '').trim().replace(',', '.');
      return /^\d+(?:\.\d+)?$/.test(text) && Number(text) > 0 ? Number(text) : null;
    };
    const dimensions = () => [...root.querySelectorAll('.dj-product-dimensions tbody tr')].map(row => ({
      handle: row.dataset.djHandle,
      width: widthNumber(row.dataset.djWidth),
      current: row.classList.contains('is-current'),
      link: row.querySelector('a[href]'),
    }));
    const currentDimension = () => dimensions().find(dimension => dimension.current);
    let pairAnchor = null;

    // Carry the chosen first width only through this model's dimension links.
    // No global preference can select 2 on an unrelated model or finish.
    const updateNavigation = active => {
      if (!active) pairAnchor = null;
      else if (!pairAnchor) pairAnchor = currentDimension();
      for (const dimension of dimensions()) {
        if (!dimension.link) continue;
        const url = new URL(dimension.link.href, window.location.href);
        url.searchParams.delete('dj_monte');
        url.searchParams.delete('dj_pair');
        if (active && pairAnchor?.handle && dimension.width
          && (dimension.handle === pairAnchor.handle || dimension.width !== pairAnchor.width)) {
          url.searchParams.set('dj_monte', '2plus2');
          url.searchParams.set('dj_pair', pairAnchor.handle);
        }
        dimension.link.setAttribute('href', url.pathname + url.search + url.hash);
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('dj_monte');
      url.searchParams.delete('dj_pair');
      if (active && pairAnchor?.handle) {
        url.searchParams.set('dj_monte', '2plus2');
        url.searchParams.set('dj_pair', pairAnchor.handle);
      }
      if (url.href !== window.location.href) {
        try { window.history.replaceState(window.history.state, '', url.href); } catch { /* Quantity still works without history access. */ }
      }
      root.querySelector('directjantes-product-dimensions')?.setStaggeredMode?.(active, pairAnchor?.handle);
    };

    const state = () => {
      const input = form.querySelector('input[name="quantity"]');
      const sameVariant = form.querySelector('input[name="id"]')?.value === this.dataset.variantId;
      const current = currentDimension();
      const hasAlternative = current?.width && dimensions().some(dimension => !dimension.current
        && dimension.link && dimension.width && dimension.width !== current.width);
      const cartQuantity = Math.max(0, Number(input?.dataset.cartQuantity) || 0);
      const minimum = Number(input?.min) || 1;
      const step = Number(input?.step) || 1;
      const ruleLimit = input?.max ? Number(input.max) : Infinity;
      const stockLimit = this.hasAttribute('data-inventory-limit') ? Number(this.dataset.inventoryLimit) : Infinity;
      const remaining = Math.min(ruleLimit, stockLimit) - cartQuantity;
      const shown = !!input && sameVariant && !!hasAlternative;
      const allowed = quantity => shown && !input.disabled && this.dataset.available === 'true'
        && quantity >= minimum && quantity <= remaining && (quantity - minimum) % step === 0;
      return { input, shown, allowed };
    };
    const update = () => {
      const { input, shown, allowed } = state();
      presets.hidden = !shown;
      fallback.hidden = shown;
      help.hidden = !shown || Number(input?.value) !== 2;
      for (const button of buttons) {
        const quantity = Number(button.dataset.djQuantity);
        button.disabled = !allowed(quantity);
        button.setAttribute('aria-pressed', String(shown && Number(input?.value) === quantity));
        button.title = button.disabled ? 'Quantité indisponible selon le stock ou les limites de commande.' : '';
      }
      updateNavigation(shown && Number(input?.value) === 2 && allowed(2));
      if (!help.hidden) {
        help.textContent = pairAnchor?.handle && pairAnchor.handle !== this.dataset.productHandle
          ? 'Mode 2 + 2 conservé : ajoutez 2 jantes de cette largeur.'
          : 'Ajoutez cette paire, puis choisissez une largeur différente dans le tableau.';
      }
    };
    const setQuantity = quantity => {
      const { input, allowed } = state();
      if (!allowed(quantity)) return false;
      input.value = String(quantity);
      const selector = input.closest('quantity-selector-component');
      selector?.updateButtonStates?.();
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      // Use the native event so delivery and the sticky button receive the same quantity.
      if (typeof selector?.onQuantityChange === 'function') selector.onQuantityChange();
      else input.dispatchEvent(new CustomEvent('quantity-selector:update', { bubbles: true, detail: { quantity } }));
      return true;
    };
    for (const button of buttons) {
      button.addEventListener('click', () => {
        const quantity = Number(button.dataset.djQuantity);
        setQuantity(quantity);
        update();
      }, { signal: this.#controller.signal });
    }
    for (const event of ['input', 'change', 'quantity-selector:update']) {
      form.addEventListener(event, update, { signal: this.#controller.signal });
    }
    this.#observer = new MutationObserver(update);
    this.#observer.observe(form, { subtree: true, childList: true, attributes: true,
      attributeFilter: ['min', 'max', 'step', 'disabled', 'data-cart-quantity', 'value'] });
    window.addEventListener('pageshow', update, { signal: this.#controller.signal });
    const incoming = new URL(window.location.href).searchParams;
    const current = currentDimension();
    const origin = dimensions().find(dimension => dimension.handle && dimension.handle === incoming.get('dj_pair'));
    if (incoming.get('dj_monte') === '2plus2' && origin?.width && current?.width
      && (origin.current || origin.width !== current.width) && state().allowed(2)) {
      pairAnchor = origin;
      setQuantity(2);
    }
    update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }
}

if (!customElements.get('directjantes-product-quantity')) {
  customElements.define('directjantes-product-quantity', DirectJantesProductQuantity);
}

/** Native details keeps finish links usable without JavaScript; closing adds no layout shift. */
class DirectJantesProductFinishes extends HTMLElement {
  #controller;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const details = this.querySelector('details');
    const summary = details?.querySelector('summary');
    if (!details || !summary) return;
    const signal = this.#controller.signal;
    document.addEventListener('pointerdown', event => {
      if (details.open && !this.contains(event.target)) details.open = false;
    }, { signal });
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape' && details.open) {
        details.open = false;
        summary.focus();
        event.preventDefault();
        event.stopPropagation();
      }
    }, { signal });
    this.addEventListener('focusout', event => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) details.open = false;
    }, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }
}

if (!customElements.get('directjantes-product-finishes')) {
  customElements.define('directjantes-product-finishes', DirectJantesProductFinishes);
}
